/* eslint-disable react-hooks/exhaustive-deps */
import { MutableRefObject, RefObject, useCallback } from "react";
import { errorService, t } from "app/utils";
import { UnsplashImageResponse } from "chq";
import { useSignUpload } from "app/hooks";
import Editor from "components/editor/Editor";
import { VideoDimension } from "components/stories/types";
import useUploadImages from "./useUploadImages";
import useUploadVideo from "./useUploadVideos";
import useAddTags from "./useAddTags";
import usePostStory from "./usePostStory";
import { isMediaVideoType } from "../utils";
import { Media, PostStoryProviderProps, PostStoryState, SetState } from "../types";

type UseHandleSubmit = {
  videoDurationRef: MutableRefObject<number | undefined>;
  editorRef: RefObject<Editor | undefined>;
  editorCopyRef: RefObject<Editor | undefined>;
  aiStoryBodyRef: RefObject<Editor | undefined>;
  selectedOptionsRef: MutableRefObject<any[] | undefined>;
  videoDimensionRef: MutableRefObject<VideoDimension | undefined>;
  prompts: PostStoryProviderProps["prompts"];
  state: PostStoryState;
  setState: SetState;
  onAnotherModalOpen: PostStoryProviderProps["onAnotherModalOpen"];
  onNotification: PostStoryProviderProps["onNotification"];
  onPromptChange: PostStoryProviderProps["onPromptChange"];
  onPosted: PostStoryProviderProps["onPosted"];
  onClose: () => void;
  closeModal: () => void;
  restartState: () => void;
};

const useHandleSubmit = ({
  videoDurationRef,
  videoDimensionRef,
  editorRef,
  editorCopyRef,
  aiStoryBodyRef,
  selectedOptionsRef,
  prompts,
  state: {
    userSelectedImage,
    progress,
    selectedPrompt,
    selectedQuestion,
    selectedImages,
    selectedMedia,
    selectedChannel,
    thumbnail,
    mappersValueIds,
    mediaData,
    webVideoRecorded,
    currentStory,
    questions,
    suggestions,
    selectedVideos
  },
  setState,
  onAnotherModalOpen,
  onNotification,
  onPromptChange,
  onPosted,
  onClose,
  closeModal,
  restartState
}: UseHandleSubmit) => {
  const signUpload = useSignUpload();
  const uploadImages = useUploadImages({ setState });
  const uploadVideos = useUploadVideo({ setState });
  const addTags = useAddTags({ tags: selectedOptionsRef.current });
  const waitForS3 = usePostStory({
    state: { selectedMedia, selectedPrompt, webVideoRecorded, currentStory },
    setState,
    videoDimensionRef,
    prompts,
    onAnotherModalOpen,
    onNotification,
    onPromptChange,
    onPosted,
    onClose,
    closeModal,
    restartState
  });

  return useCallback(
    async ({ forceSubmit, newPublicUser } = {}) => {
      if (!userSelectedImage) {
        switch (selectedPrompt?.allowedTypes) {
          case "just_videos":
            setState({ error: t("Please select a video to share") });
            break;
          case "just_images":
            setState({ error: t("Please select an image to share") });
            break;
          default:
            setState({ error: t("Please select an image or video to share") });
            break;
        }
        return;
      }

      const questionId = selectedPrompt?.id || selectedQuestion;
      if (!questionId) {
        setState({ error: t("Please select a prompt in order to post your story") });
        return;
      }

      if (!editorCopyRef.current && !editorRef.current && !aiStoryBodyRef.current) {
        return;
      }

      setState({ postingStory: true });
      if (progress === 0 && !forceSubmit) setState({ progress: 1 });

      const post = async () => {
        let mediaType;
        let videoLength = 0;
        let avatarUrl;
        let visuals: Media[] = [];
        let mediaToUpload = newPublicUser?.avatarUrl ? 1 : 0;

        if (selectedImages.length > 0) {
          mediaType = "image";
          try {
            visuals = (await uploadImages(
              selectedImages as (Media & UnsplashImageResponse)[]
            )) as Media[];
          } catch (error) {
            let errorMessage = "Unknown error occurred while uploading images";

            // Handle XMLHttpRequest upload errors
            if (error && typeof error === "object" && "type" in error && error.type === "error") {
              const target = (error as any).target as XMLHttpRequest;
              if (target) {
                if (target.status === 0) {
                  errorMessage = `Network error: Please check your internet connection: ${target.responseText || target.statusText || "Unknown error"}`;
                } else if (target.status === 403) {
                  errorMessage = `Permission denied: Upload authorization failed: ${target.responseText || target.statusText || "Unknown error"}`;
                } else if (target.status === 413) {
                  errorMessage = `Image file is too large for upload: ${target.responseText || target.statusText || "Unknown error"}`;
                } else {
                  errorMessage = `Upload failed (Status ${target.status}): ${target.responseText || target.statusText || "Unknown error"}`;
                }
              }
            } else if (error instanceof Error) {
              errorMessage = error.message;
            } else if (error && typeof error === "object") {
              errorMessage = JSON.stringify(error);
            }

            errorService.error(`Error uploading images: ${errorMessage}`);
            throw new Error(`Failed to upload images: ${errorMessage}`);
          }

          if (newPublicUser?.avatarUrl) {
            try {
              avatarUrl = await signUpload(
                newPublicUser.avatarUrl,
                (mediaProgress: number) => {
                  setState({ progress: 90 + mediaProgress / 10 });
                }
              );
            } catch (error) {
              errorService.error(`Error uploading avatar: ${error}`);
              throw new Error("Failed to upload avatar. Please try again.");
            }
          }
        } else if (
          isMediaVideoType(selectedMedia as Blob)
          && (selectedMedia as Blob).size
        ) {
          // This case is a video
          mediaType = "video";
          videoLength = videoDurationRef.current || 0;

          try {
            let visualsThumb;
            if (!mediaData && thumbnail) {
              visualsThumb = await signUpload(thumbnail);
            }

            if (selectedVideos) {
              videoLength = 0;
              mediaToUpload += selectedVideos.length;

              const getVideoDuration = (videoFile: File): Promise<number> => (
                new Promise(resolve => {
                  const videoElement = document.createElement("video");

                  // Set a timeout to prevent hanging
                  const timeoutId = setTimeout(() => {
                    URL.revokeObjectURL(videoElement.src);
                    // If we timeout, return a fallback duration based on file size
                    // Assuming roughly 1MB per second of video as a fallback
                    const estimatedDuration = Math.max(1, videoFile.size / (1024 * 1024));
                    resolve(estimatedDuration);
                  }, 5000); // 5 second timeout

                  videoElement.onerror = (e: any) => {
                    clearTimeout(timeoutId);
                    const { error } = videoElement;
                    URL.revokeObjectURL(videoElement.src);
                    // If there's an error, still try to proceed with an estimated duration
                    const estimatedDuration = Math.max(1, videoFile.size / (1024 * 1024));
                    resolve(estimatedDuration);
                  };

                  videoElement.onloadedmetadata = () => {
                    clearTimeout(timeoutId);
                    const { duration } = videoElement;
                    URL.revokeObjectURL(videoElement.src);
                    resolve(duration);
                  };

                  // Create object URL and set video properties
                  const objectUrl = URL.createObjectURL(videoFile);
                  videoElement.src = objectUrl;
                  videoElement.preload = "metadata";
                })
              );

              try {
                const allDurations = await Promise.all(
                  selectedVideos.map(async video => {
                    try {
                      return await getVideoDuration(video);
                    } catch (e) {
                      // Fallback to size-based estimate if duration calculation fails
                      return Math.max(1, video.size / (1024 * 1024));
                    }
                  })
                );
                videoLength = allDurations.reduce(
                  (accumulator, current) => accumulator + current, 0
                );
              } catch (error) {
                // If all else fails, use a conservative estimate based on file sizes
                videoLength = selectedVideos.reduce(
                  (acc, video) => acc + Math.max(1, video.size / (1024 * 1024)), 0
                );
              }

              try {
                visuals = (await uploadVideos(selectedVideos, visualsThumb)) as Media[];
              } catch (error) {
                let errorMessage = "Unknown error occurred while uploading videos";

                // Handle XMLHttpRequest upload errors
                if (error && typeof error === "object" && "type" in error && error.type === "error") {
                  const target = (error as any).target as XMLHttpRequest;
                  if (target) {
                    if (target.status === 0) {
                      errorMessage = `Network error: Please check your internet connection: ${target.responseText || target.statusText || "Unknown error"}`;
                    } else if (target.status === 403) {
                      errorMessage = `Permission denied: Upload authorization failed: ${target.responseText || target.statusText || "Unknown error"}`;
                    } else if (target.status === 413) {
                      errorMessage = `Video file is too large for upload: ${target.responseText || target.statusText || "Unknown error"}`;
                    } else {
                      errorMessage = `Upload failed (Status ${target.status}): ${target.responseText || target.statusText || "Unknown error"}`;
                    }
                  }
                } else if (error instanceof Error) {
                  errorMessage = error.message;
                } else if (error && typeof error === "object") {
                  errorMessage = JSON.stringify(error);
                }

                errorService.error(`Error uploading videos: ${errorMessage}`);
                throw new Error(`Failed to upload videos: ${errorMessage}`);
              }
            }

            if (newPublicUser?.avatarUrl) {
              try {
                avatarUrl = await signUpload(
                  newPublicUser.avatarUrl,
                  (mediaProgress: number) => {
                    const progressRatio = progress === 1 ? 0.75 : 0.9;
                    setState({
                      progress:
                        (progressRatio * (100 + mediaProgress)) / mediaToUpload
                    });
                  }
                );
              } catch (error) {
                errorService.error(`Failed to upload avatar: ${error}`);
                throw new Error("Failed to upload avatar. Please try again.");
              }
            }
          } catch (error) {
            errorService.error(`Failed to process video upload: ${error}`);
            throw new Error("Failed to process video upload. Please try again.");
          }
        }

        const body = aiStoryBodyRef.current || (await editorRef.current?.serialize())
          || (await editorCopyRef.current?.serialize())
          || "";
        const bodyWithTags = addTags(body as string);

        await waitForS3(
          2000,
          questionId,
          bodyWithTags,
          selectedChannel,
          visuals,
          mediaType,
          videoLength,
          mediaData,
          newPublicUser
            ? { ...newPublicUser, avatarUrl }
            : undefined,
          questions,
          suggestions,
          mappersValueIds
        );
      };

      setState({ error: undefined });
      post();
    },
    [
      addTags,
      editorCopyRef,
      editorRef,
      aiStoryBodyRef,
      mediaData,
      progress,
      selectedChannel,
      selectedImages,
      selectedMedia,
      selectedPrompt,
      selectedQuestion,
      questions,
      suggestions,
      setState,
      signUpload,
      thumbnail,
      uploadImages,
      uploadVideos,
      userSelectedImage,
      videoDurationRef,
      waitForS3
    ]
  );
};

export default useHandleSubmit;
